/**
 * The palette.
 *
 * Anchored on the Talas wordmark cyan (#48c4e5), which happens to be exactly the colour of
 * shallow water — the brand and the metaphor were already the same thing before anyone drew
 * anything. Everything else is built around it: deep sea below, sand and grass above.
 *
 * Deliberately bright and high-contrast. The audience is mostly young people who grew up on GBA
 * palettes, and the app is used for two minutes at a time in daylight on a phone. A moody dark
 * interface would read as "another dashboard".
 */

export const palette = {
  /* --- sea --- */
  seaDeep: '#0b3358',
  seaDeepShade: '#08294a',
  sea: '#1668a8',
  seaLight: '#2b8fce',
  shallow: '#48c4e5',
  shallowLight: '#7ad8ef',
  foam: '#cdf1fa',

  /* --- land --- */
  sand: '#f0dda6',
  sandDark: '#d6ba7c',
  sandWet: '#c9a86a',
  grass: '#4aae4b',
  grassLight: '#6fd05c',
  grassDark: '#2f7f3d',
  grassDeep: '#1f5d31',
  path: '#e8cf94',
  pathDark: '#c8a969',
  rock: '#7e8ea6',
  rockDark: '#4d5c74',
  rockLight: '#a3b2c6',
  cliff: '#8b6b4a',
  cliffDark: '#5e4531',
  treeDark: '#1d6338',
  treeMid: '#2f8a49',
  treeLight: '#54b862',
  trunk: '#6b4426',
  wood: '#b4813f',
  woodDark: '#7d5626',

  /* --- things you can paint on an island in the world builder --- */
  bloom: '#ff7a8a',
  bloomAlt: '#ffd166',
  bloomPale: '#fdf6e3',
  bush: '#3f9a55',
  bushDark: '#256b3a',
  fence: '#c08b4e',
  fenceDark: '#8a5f2f',
  hutWall: '#dcbd90',
  hutWallDark: '#a8854f',
  roof: '#b3503c',
  roofDark: '#7d3327',
  tallGrass: '#3f9c4b',
  tallGrassDark: '#27713a',

  /* --- interface --- */
  ink: '#0b2740',
  inkSoft: '#3d6280',
  paper: '#f5fafc',
  paperShade: '#d3e6ee',
  paperEdge: '#9dc0d1',
  accent: '#48c4e5',
  accentDark: '#1f8fb4',
  gold: '#ffc247',
  goldDark: '#c78a12',
  green: '#4fc27b',
  greenDark: '#2c8a52',
  warn: '#e2604f',
  night: '#061a2c',

  /* --- character ---
   *
   * Four ramps, and the rule for all four is the same: **the first four entries never move.** They
   * are what the eight original looks decode to, and reordering one of them would repaint several
   * hundred people. Everything after index 3 was added for the editor and is free to grow — up to
   * the slot widths frozen in `core/look.ts`.
   *
   * Every entry has to survive being reduced to about forty pixels at map scale, which rules out
   * two things that look fine on a swatch: anything within a shade of the black outline, and any
   * two entries a viewer could not tell apart side by side. Both were tested by drawing them. */

  /** Eight tones, light to deep, spaced so no two neighbours read as the same person. */
  skin: [
    '#f3ceaa',
    '#e0aa7d',
    '#c98a5e',
    '#8d5a3a',
    '#f7ddc4',
    '#b5764a',
    '#6b3f26',
    '#e8b98c',
  ],

  /**
   * Twelve hair colours: seven that grow out of a head and five that come out of a bottle.
   *
   * The dyed half is not a joke. This is a community of editors, half of them under twenty-five,
   * and „koja ti je boja kose" has been a real answer in that room for a decade. The teal is the
   * exact cyan of the Talas wordmark, which is also the streak in Marko's fringe.
   */
  hair: [
    '#3a2a1f',
    '#7a4a24',
    '#1a1a22',
    '#c9a227',
    '#e2d6ac',
    '#5c3a2a',
    '#b5541f',
    '#b9c2cc',
    '#2f93ad',
    '#e05a9b',
    '#8a5cd6',
    '#3f6fd0',
  ],

  /** Twelve shirt colours. The first is the wordmark cyan, which is the game's default character. */
  shirt: [
    '#48c4e5',
    '#e2604f',
    '#7a5cd6',
    '#4fc27b',
    '#2a303c',
    '#f1ecdf',
    '#1f3f6b',
    '#e08b3a',
    '#f0dda6',
    '#f0c243',
    '#e88fb0',
    '#8f2733',
  ],

  /** Eight trouser colours. The first is the blue-grey every character wore before this existed. */
  trousers: [
    '#2f4a63',
    '#22252e',
    '#3f6396',
    '#8a7a52',
    '#4a5a34',
    '#5a5f68',
    '#5a4030',
    '#6b2f3a',
  ],

  /** Six eye colours. Index 0 is the near-black the eyes were when they were part of the outline. */
  eyes: ['#141b24', '#6b4426', '#3f79b0', '#3f8f63', '#7d8a99', '#a8763a'],

  white: '#ffffff',
  black: '#0a1520',
} as const;

/** Islands each get a look. New biomes fall back to the first entry rather than crashing. */
export interface Biome {
  ground: string;
  groundAlt: string;
  groundDark: string;
  edge: string;
  accent: string;
  /** The trodden route through the island. Must read clearly against `ground`. */
  path: string;
  pathDark: string;
  tree: [string, string, string];
  label: string;
}

export const biomes: Record<string, Biome> = {
  // Shallows: a sandbar you can stand on. Nothing here can go wrong.
  plicak: {
    ground: palette.sand,
    groundAlt: palette.sandDark,
    groundDark: palette.sandWet,
    edge: palette.shallowLight,
    accent: palette.shallow,
    // A boardwalk, not a dirt track — a dirt path on sand is invisible.
    path: palette.wood,
    pathDark: palette.woodDark,
    tree: [palette.treeLight, palette.treeMid, palette.trunk],
    label: 'peščani sprud',
  },
  // Foundation: green, solid, a lighthouse. The first thing you build that stands up on its own.
  temelj: {
    ground: palette.grass,
    groundAlt: palette.grassLight,
    groundDark: palette.grassDark,
    edge: palette.sand,
    accent: palette.grassDark,
    path: palette.path,
    pathDark: palette.pathDark,
    tree: [palette.treeLight, palette.treeMid, palette.trunk],
    label: 'zelena obala',
  },
  // The Cut: an island split in half by a canyon. The name is the terrain.
  rez: {
    ground: palette.grassDark,
    groundAlt: palette.grass,
    groundDark: palette.grassDeep,
    edge: palette.cliff,
    accent: palette.cliffDark,
    path: palette.rockLight,
    pathDark: palette.rockDark,
    tree: [palette.treeMid, palette.treeDark, palette.trunk],
    label: 'kanjon',
  },
  /**
   * The practice ground. Deliberately the least dramatic island on the archipelago: worn grass and
   * bare tracks, a place that looks used rather than conquered. Nothing here is a landmark, because
   * nothing here is a milestone — you come back to it over and over.
   */
  vezbaliste: {
    // Worn ground, not pasture. Sand where a course island would be green, so the biggest island on
    // the map does not read as the most important one — walked-over, not conquered.
    ground: palette.sandDark,
    groundAlt: palette.sand,
    groundDark: palette.sandWet,
    edge: palette.grassDark,
    accent: palette.wood,
    path: palette.cliff,
    pathDark: palette.cliffDark,
    tree: [palette.treeMid, palette.treeDark, palette.trunk],
    label: 'vežbalište',
  },
};

export function biomeFor(name: string | undefined): Biome {
  return (name && biomes[name]) || biomes.plicak!;
}
